"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  BALANCED_EQUATION_STRINGS,
  BalancedEquationCard,
  EquationField,
} from "@/components/tools/chem-reactions/balanced-equation";
import {
  GAS_CONDITIONS_STRINGS,
  GasConditionsFields,
} from "@/components/tools/chem-reactions/gas-conditions";
import {
  type AmountState,
  patchAmount,
  REACTANT_AMOUNT_STRINGS,
  ReactantAmountFields,
  readAmounts,
} from "@/components/tools/chem-reactions/reactant-amounts";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  DEFAULT_GAS_CONDITIONS,
  findAmountUnit,
  type GasConditionsInput,
  resolveMolarVolume,
} from "@/lib/tool-kit/chem-reactions/amounts";
import {
  type BalancedEquation,
  solveBalanced,
} from "@/lib/tool-kit/chem-reactions/balance";
import {
  type LimitingResult,
  limitingReactant,
} from "@/lib/tool-kit/chem-reactions/limiting";
import {
  fewestSigFigs,
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/chemistry/format";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const LIMITING_REACTANT_CALCULATOR_STRINGS = {
  ...BALANCED_EQUATION_STRINGS,
  ...GAS_CONDITIONS_STRINGS,
  ...REACTANT_AMOUNT_STRINGS,
  equationPlaceholder: "N2(g) + H2(g) -> NH3(g)",
  results: "Results",
  limiting: "Limiting reactant",
  limitingBoth: "Limiting reactants (tied)",
  theoretical: "Theoretical yield of {species}",
  excessSummary: "Left over of {species}",
  none: "None left over",
  noAmounts: "Enter an amount for at least one reactant.",
  reactantsHeading: "Reactants",
  columnSpecies: "Species",
  columnAvailable: "Available (mol)",
  columnCoefficient: "Coefficient",
  columnQuotient: "Moles ÷ coefficient",
  columnUsed: "Used (mol)",
  columnLeftMoles: "Left over (mol)",
  columnLeftMass: "Left over (g)",
  columnStatus: "Status",
  statusLimiting: "Limiting",
  statusExcess: "In excess",
  statusUnlimited: "Unlimited supply",
  noValue: "—",
  productsHeading: "Theoretical yield",
  columnMoles: "Moles (mol)",
  columnMass: "Mass (g)",
  columnVolume: "Gas volume (L)",
  tieNote:
    "These reactants are in exactly the ratio of the equation, so both run out together and nothing is left over.",
  workingHeading: "The working",
  moleLine: "n({species}) = {value} {unit} ÷ {molar} g/mol = {moles} mol",
  volumeLine: "n({species}) = {value} L ÷ {molarVolume} L/mol = {moles} mol",
  quotientLine: "{species}: {moles} mol ÷ {coefficient} = {quotient}",
  pickLine:
    "The smallest quotient, {extent}, belongs to {species}, so it limits the reaction. Each product is its coefficient × {extent}.",
  pickTieLine:
    "The smallest quotient, {extent}, is shared by {species}, so they limit the reaction together. Each product is its coefficient × {extent}.",
  sigNote:
    "Rounded to {count} significant figures, the fewest in the amounts you entered.",
  approxNote:
    "{species} has no IUPAC standard atomic weight, so its mass number is used and its mass is approximate.",
  copy: "Copy results",
  copied: "Copied!",
  copyLine: "Limiting reactant: {species}",
  copyYield: "{species}: {moles} mol, {grams} g",
} as const;

type Strings = typeof LIMITING_REACTANT_CALCULATOR_STRINGS;

const EXAMPLES = [
  "N2(g) + H2(g) -> NH3(g)",
  "Al + Cl2 -> AlCl3",
  "C3H8(g) + O2(g) -> CO2(g) + H2O(l)",
  "Na + H2O -> NaOH + H2(g)",
] as const;
const DEFAULT_AMOUNTS = ["50.0", "10.0"];
const DEFAULT_SIG = 4;
const GUARD = 1;
interface ViewProps {
  result: LimitingResult;
  states: readonly AmountState[];
  molarVolumeValue: number | null;
  sig: number;
  strings: Strings;
  fmt: NumberFormatter;
}

function ReactantTable({ result, sig, strings, fmt }: ViewProps) {
  const cell = (value: number | null, digits = sig) =>
    value === null ? strings.noValue : formatSig(fmt, value, digits);
  const status = (row: LimitingResult["reactants"][number]) => {
    if (row.available === null) {
      return strings.statusUnlimited;
    }
    return row.limiting ? strings.statusLimiting : strings.statusExcess;
  };
  return (
    <DataTable
      head={[
        strings.columnSpecies,
        strings.columnAvailable,
        strings.columnCoefficient,
        strings.columnQuotient,
        strings.columnUsed,
        strings.columnLeftMoles,
        strings.columnLeftMass,
        strings.columnStatus,
      ]}
      rows={result.reactants.map((row) => [
        row.species.label,
        cell(row.available),
        fmt(row.coefficient),
        cell(row.perCoefficient),
        cell(row.used),
        row.limiting && row.available !== null ? fmt(0) : cell(row.leftover),
        row.limiting && row.available !== null
          ? fmt(0)
          : cell(row.leftoverGrams),
        status(row),
      ])}
    />
  );
}

function ProductTable({ result, sig, strings, fmt }: ViewProps) {
  const showVolume = result.products.some((row) => row.litres !== null);
  const cell = (value: number | null) =>
    value === null ? strings.noValue : formatSig(fmt, value, sig);
  return (
    <DataTable
      head={[
        strings.columnSpecies,
        strings.columnMoles,
        strings.columnMass,
        ...(showVolume ? [strings.columnVolume] : []),
      ]}
      rows={result.products.map((row) => [
        row.species.label,
        cell(row.moles),
        cell(row.grams),
        ...(showVolume ? [cell(row.litres)] : []),
      ])}
    />
  );
}

function workingLines(props: ViewProps): string[] {
  const { result, states, strings, fmt, sig, molarVolumeValue } = props;
  const guard = sig + GUARD;
  const lines: string[] = [];
  for (const row of result.reactants) {
    if (row.available === null) {
      continue;
    }
    const state = states[row.index] ?? { value: "", unit: "g" };
    const unit = findAmountUnit(state.unit);
    const value = parseMeasuredNumber(state.value) ?? 0;
    const moles = formatSig(fmt, row.available, guard);
    if (unit.kind === "mass") {
      lines.push(
        t(strings.moleLine, {
          species: row.species.label,
          value: formatSig(fmt, value * unit.factor, guard),
          unit: "g",
          molar: formatSig(fmt, row.molarMass ?? 0, guard),
          moles,
        })
      );
    } else if (unit.kind === "volume") {
      lines.push(
        t(strings.volumeLine, {
          species: row.species.label,
          value: formatSig(fmt, value * unit.factor, guard),
          molarVolume: formatSig(fmt, molarVolumeValue ?? 0, guard),
          moles,
        })
      );
    }
    lines.push(
      t(strings.quotientLine, {
        species: row.species.label,
        moles,
        coefficient: fmt(row.coefficient),
        quotient: formatSig(fmt, row.perCoefficient ?? 0, guard),
      })
    );
  }
  const names = result.reactants
    .filter((row) => row.limiting)
    .map((row) => row.species.label)
    .join(", ");
  lines.push(
    t(result.tie ? strings.pickTieLine : strings.pickLine, {
      extent: formatSig(fmt, result.extent, guard),
      species: names,
    })
  );
  return lines;
}

function Answer(props: ViewProps) {
  const { result, strings, fmt, sig } = props;
  const limiting = result.reactants.filter((row) => row.limiting);
  const names = limiting.map((row) => row.species.label).join(", ");
  const lines = workingLines(props);
  const copy = [
    t(strings.copyLine, { species: names }),
    ...result.products.map((row) =>
      t(strings.copyYield, {
        species: row.species.label,
        moles: formatSig(fmt, row.moles, sig),
        grams:
          row.grams === null ? strings.noValue : formatSig(fmt, row.grams, sig),
      })
    ),
  ].join("\n");
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={result.tie ? strings.limitingBoth : strings.limiting}
          value={names}
        />
        {result.products.slice(0, 2).map((row) => (
          <StatCard
            key={row.index}
            label={t(strings.theoretical, { species: row.species.label })}
            value={
              row.grams === null
                ? `${formatSig(fmt, row.moles, sig)} mol`
                : `${formatSig(fmt, row.grams, sig)} g`
            }
          />
        ))}
      </StatGrid>
      {result.tie ? <Notice>{strings.tieNote}</Notice> : null}
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.reactantsHeading}</h3>
        <ReactantTable {...props} />
      </div>
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.productsHeading}</h3>
        <ProductTable {...props} />
      </div>
      {result.approximate.length > 0 ? (
        <Notice>
          {t(strings.approxNote, { species: result.approximate.join(", ") })}
        </Notice>
      ) : null}
      <Notice>{t(strings.sigNote, { count: fmt(sig) })}</Notice>
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
        <div className={WORKING_BOX}>
          {lines.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={copy}
      />
    </>
  );
}

function Calculation({
  solved,
  states,
  gas,
  strings,
  fmt,
}: {
  solved: Extract<BalancedEquation, { kind: "ok" }>;
  states: readonly AmountState[];
  gas: GasConditionsInput;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const species = solved.equation.species;
  const molarVolumeValue = resolveMolarVolume(gas);
  const read = readAmounts(species, states, molarVolumeValue, strings);
  if (!read.ok) {
    return <ErrorNotice>{read.message}</ErrorNotice>;
  }
  const result = limitingReactant(
    species,
    solved.coefficients,
    read.moles,
    molarVolumeValue
  );
  if (result === null) {
    return <Notice>{strings.noAmounts}</Notice>;
  }
  return (
    <Answer
      fmt={fmt}
      molarVolumeValue={molarVolumeValue}
      result={result}
      sig={fewestSigFigs(read.raws, DEFAULT_SIG)}
      states={states}
      strings={strings}
    />
  );
}

export default function LimitingReactantCalculatorWidget() {
  const strings = useToolUi(LIMITING_REACTANT_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [equation, setEquation] = useState<string>(EXAMPLES[0]);
  const [states, setStates] = useState<AmountState[]>(
    DEFAULT_AMOUNTS.map((value) => ({ value, unit: "g" }))
  );
  const [gas, setGas] = useState<GasConditionsInput>(DEFAULT_GAS_CONDITIONS);
  const solved = solveBalanced(equation);

  const update = (index: number, patch: Partial<AmountState>) =>
    setStates(patchAmount(states, index, patch));

  const species = solved.kind === "ok" ? solved.equation.species : [];
  const anyVolume = species.some(
    (item, index) =>
      item.side === "reactant" &&
      findAmountUnit(states[index]?.unit ?? "g").kind === "volume"
  );
  const anyGas = species.some((item) => item.state === "g");

  return (
    <>
      <EquationField
        examples={EXAMPLES}
        id="lr-equation"
        onChange={setEquation}
        strings={strings}
        value={equation}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <BalancedEquationCard solved={solved} strings={strings} />
        {solved.kind === "ok" ? (
          <>
            <ReactantAmountFields
              idPrefix="lr"
              onChange={update}
              species={species}
              states={states}
              strings={strings}
            />
            {anyGas || anyVolume ? (
              <GasConditionsFields
                fmt={fmt}
                id="lr-gas"
                onChange={setGas}
                strings={strings}
                value={gas}
              />
            ) : null}
            <Calculation
              fmt={fmt}
              gas={gas}
              solved={solved}
              states={states}
              strings={strings}
            />
          </>
        ) : null}
      </section>
    </>
  );
}
